import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import LineTypeBadge from 'src/components/LineTypeBadge';
import CpNumberIndicator from 'src/components/CpNumberIndicator';
import { isPhoneNonTextable } from 'src/views/management/ChatView/contactPhones';
import helpers from 'src/helpers';

/**
 * SendToPhone — the contact's only number, shown where the selector would be.
 *
 * CON-1705. A contact with one phone gets no PhoneSelector, because there is
 * nothing to select; before this the conversation view showed her number
 * nowhere at all, so the line type, carrier and blocked-region chip reached
 * only the contacts that happened to have two numbers. That is the minority.
 *
 * This is DISPLAY ONLY — deliberately not a one-item dropdown and not a
 * disabled Select, either of which would offer a choice that does not exist.
 * It renders in the selector's slot and matches its collapsed appearance, so
 * "which number is this going to, and can it receive texts" is answered in the
 * same place whether the contact has one number or three.
 *
 * The carrier is inline here rather than hidden behind the badge tooltip.
 * The dropdown can afford to keep carrier in its expanded list; this has no
 * expanded list, and carrier is a first-class reason to look at this row —
 * carrier-level filtering of consultant traffic is rising.
 */
function SendToPhone({ phone, phoneInfo = null }) {
  if (helpers.isEmpty(phone)) return null;

  const isNonTextable = isPhoneNonTextable(phone, phoneInfo);
  // CON-1896 — a CP number shows the CP dot instead of the line type badge.
  // The server sends no carrier for it, so the carrier line drops out too.
  const isCpNumber = phoneInfo?.isCpNumber === true;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', minWidth: 0, color: '#545454',
    }}
    >
      {isNonTextable
        ? <PhoneDisabledIcon fontSize="inherit" sx={{ color: '#F44336' }} />
        : <LocalPhoneOutlinedIcon fontSize="inherit" />}
      <Box sx={{ minWidth: 0, marginLeft: '4px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="caption">
            {helpers.transform.toPhoneNumber(phone)}
          </Typography>
          {/*
            Guard is on the PHONE, not on lineType — matching the
            PhoneDetailsRow fix in CON-1704. A blocked-region number carries
            line_type null, so a lineType guard would skip the badge on exactly
            the numbers that most need one. The badge returns null by itself
            when there is neither a line type nor a blocked region.
          */}
          {isCpNumber ? (
            <CpNumberIndicator />
          ) : (
            <LineTypeBadge
              phoneNumber={phone}
              lineType={phoneInfo?.lineType}
              displayLineType={phoneInfo?.displayLineType}
              isTextable={phoneInfo?.isTextable}
              carrier={phoneInfo?.carrier}
              checkedAtRelative={phoneInfo?.checkedAtRelative}
            />
          )}
        </Box>
        {/*
          Carrier sits BELOW the number rather than beside it, because width in
          this row is contested and height is not. It shares the row with the
          voice-memo charge notice, the AI research/feedback/notes icons — which
          are pinned right and explicitly flexShrink: 0 — and the character
          counter; on a phone, an inline carrier had nowhere to go. Stacking
          costs about fourteen pixels of height, takes no width at all, and
          means the carrier survives at every screen size instead of being
          hidden on the small ones — which is the point, since carrier-level
          filtering is a first-class reason to look at this row.

          It also makes this display structurally identical to the dropdown's
          list items, which already stack carrier under the number, so the
          single-phone and multi-phone cases read the same.

          Still capped: "Hancock Rural Telephone Corp. Dba Ninestar Connect" is
          a real carrier on QA at 49 characters. The badge tooltip always
          carries the full name.
        */}
        {!helpers.isEmpty(phoneInfo?.carrier) && (
          <Typography
            variant="caption"
            noWrap
            sx={{
              display: 'block',
              color: 'inherit',
              opacity: 0.75,
              fontSize: '11px',
              lineHeight: 1.3,
              // Responsive, not fixed: desktop has room to show a long carrier
              // whole, a phone does not, and this row also carries the
              // voice-memo notice and the AI icons on the same line.
              maxWidth: { xs: '120px', md: '240px' },
            }}
          >
            {phoneInfo.carrier}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

SendToPhone.propTypes = {
  phone: PropTypes.string,
  phoneInfo: PropTypes.shape({
    lineType: PropTypes.string,
    displayLineType: PropTypes.string,
    isTextable: PropTypes.bool,
    carrier: PropTypes.string,
    checkedAtRelative: PropTypes.string,
    isCpNumber: PropTypes.bool,
  }),
};

export default SendToPhone;
